'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';

import Icon from '@/components/ui/AppIcon';
import SentimentBadge, { SentimentType } from '@/components/ui/SentimentBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import ChannelBadge from '@/components/ui/ChannelBadge';
import { getDashboardDate, getDemoCurrentTime } from '@/lib/demoDate';

const kpis = [
  { label: 'Total Interactions Today', value: '12,842', delta: '+8.4%', positive: true, icon: 'ChatBubbleLeftRightIcon', color: 'text-blue-600', bg: 'bg-blue-50' },
  { label: 'Active Agents', value: '287 / 310', delta: '92.6% utilisation', positive: true, icon: 'UsersIcon', color: 'text-green-600', bg: 'bg-green-50' },
  { label: 'Open Cases', value: '1,248', delta: '+42 since 08:00', positive: false, icon: 'ClipboardDocumentListIcon', color: 'text-amber-600', bg: 'bg-amber-50' },
  { label: 'SLA Compliance', value: '94.7%', delta: '-1.2% vs target', positive: false, icon: 'ShieldCheckIcon', color: 'text-orange-600', bg: 'bg-orange-50' },
  { label: 'Avg. Handling Time', value: '04:21', delta: '+0:03 vs target', positive: false, icon: 'ClockIcon', color: 'text-purple-600', bg: 'bg-purple-50' },
  { label: 'First Contact Resolution', value: '82%', delta: '+2% vs last week', positive: true, icon: 'CheckBadgeIcon', color: 'text-teal-600', bg: 'bg-teal-50' },
];

const queueStatus = [
  { name: 'MyGCC General', waiting: 8, agents: 45, sla: '94%', longest: '2:14', status: 'ok' },
  { name: 'Immigration', waiting: 14, agents: 32, sla: '88%', longest: '4:32', status: 'risk' },
  { name: 'Health Services', waiting: 6, agents: 28, sla: '96%', longest: '1:45', status: 'ok' },
  { name: 'Transport', waiting: 3, agents: 18, sla: '98%', longest: '0:52', status: 'ok' },
  { name: 'Taxation', waiting: 11, agents: 22, sla: '91%', longest: '3:18', status: 'risk' },
];

const recentInteractions = [
  { id: 'INT-2026-14892', citizen: 'Ahmed Firdaus', channel: 'PHONE' as const, topic: 'MySejahtera Mobile Number Update', sentiment: 'Concerned' as SentimentType, agent: 'Nur Aisyah', time: '14:32', status: 'Active' },
  { id: 'INT-2026-14891', citizen: 'Nurul Huda', channel: 'WHATSAPP' as const, topic: 'Passport Renewal Documentation', sentiment: 'Frustrated' as SentimentType, agent: 'Nur Aisyah', time: '14:28', status: 'Active' },
  { id: 'INT-2026-14888', citizen: 'Kumar Rajan', channel: 'PHONE' as const, topic: 'Road Tax Renewal', sentiment: 'Satisfied' as SentimentType, agent: 'Hafizuddin', time: '14:15', status: 'Resolved' },
  { id: 'INT-2026-14887', citizen: 'Nur Aina', channel: 'WEB CHAT' as const, topic: 'MyDigital ID Setup', sentiment: 'Positive' as SentimentType, agent: 'Siti Rohaida', time: '14:10', status: 'Resolved' },
];

const announcements = [
  { title: 'System Maintenance Scheduled', desc: 'Planned maintenance on 18 Aug 2026, 02:00–04:00 MYT. All services will be temporarily unavailable.', type: 'warning', time: '2h ago' },
  { title: 'New Knowledge Article Published', desc: 'Passport Renewal Guide updated with new documentation requirements effective 15 Aug 2026.', type: 'info', time: '4h ago' },
  { title: 'SLA Policy Update', desc: 'Immigration queue SLA target updated from 4h to 3h effective 17 Aug 2026.', type: 'info', time: '1d ago' },
];

export default function HomeContent() {
  const [headerDate, setHeaderDate] = useState('');
  const [headerTime, setHeaderTime] = useState('');

  useEffect(() => {
    setHeaderDate(getDashboardDate());
    setHeaderTime(getDemoCurrentTime());
  }, []);

  return (
    <div className="max-w-screen-2xl mx-auto px-5 py-4 page-transition">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-[18px] font-700 text-foreground">MyGCC Operations Overview</h1>
          <p className="text-[12px] text-muted-foreground mt-0.5">{headerDate || 'Loading...'} · {headerTime || '--:--'} MYT · Executive Summary</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-[11px] bg-green-50 border border-green-200 text-green-700 px-2.5 py-1 rounded-lg font-600">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            All Systems Operational
          </span>
          <Link href="/" className="btn-primary px-3 py-1.5 text-[12px] flex items-center gap-1.5">
            <Icon name="RectangleStackIcon" size={13} />
            Go to My Workspace
          </Link>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
        {kpis.map(k => (
          <div key={k.label} className="card-elevated p-4 card-hover">
            <div className={`p-2 rounded-xl ${k.bg} w-fit mb-3`}>
              <Icon name={k.icon as Parameters<typeof Icon>[0]['name']} size={15} className={k.color} />
            </div>
            <div className="text-[20px] font-700 font-tabular text-foreground mb-0.5">{k.value}</div>
            <div className="text-[10px] text-muted-foreground font-500 mb-1">{k.label}</div>
            <div className={`text-[10px] font-600 flex items-center gap-0.5 ${k.positive ? 'text-green-600' : 'text-amber-600'}`}>
              <Icon name={k.positive ? 'ArrowTrendingUpIcon' : 'ArrowTrendingDownIcon'} size={10} />
              {k.delta}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Queue Status */}
        <div className="col-span-12 xl:col-span-5">
          <div className="card-elevated overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-blue-50">
                  <Icon name="QueueListIcon" size={13} className="text-blue-600" />
                </div>
                <h2 className="text-[13px] font-700">Queue Status</h2>
              </div>
              <span className="text-[11px] text-muted-foreground">Live · Updated 14:35</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-muted/40 border-b border-border">
                    {['Queue', 'Waiting', 'Agents', 'SLA', 'Longest Wait'].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-[10px] font-700 uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {queueStatus.map(q => (
                    <tr key={q.name} className="table-row-hover transition-colors">
                      <td className="px-3 py-2.5 font-600 text-foreground whitespace-nowrap">{q.name}</td>
                      <td className="px-3 py-2.5">
                        <span className={`font-700 font-tabular ${q.waiting > 10 ? 'text-amber-600' : 'text-foreground'}`}>{q.waiting}</span>
                      </td>
                      <td className="px-3 py-2.5 font-tabular text-muted-foreground">{q.agents}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[11px] font-700 ${q.status === 'risk' ? 'text-amber-600' : 'text-green-600'}`}>{q.sla}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`font-tabular text-[11px] font-600 ${q.status === 'risk' ? 'text-amber-600' : 'text-muted-foreground'}`}>{q.longest}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Recent Interactions */}
        <div className="col-span-12 xl:col-span-4">
          <div className="card-elevated overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-purple-50">
                  <Icon name="ChatBubbleLeftRightIcon" size={13} className="text-purple-600" />
                </div>
                <h2 className="text-[13px] font-700">Recent Interactions</h2>
              </div>
              <Link href="/interactions" className="text-[11px] text-primary font-600 hover:underline">View all →</Link>
            </div>
            <div className="divide-y divide-border">
              {recentInteractions.map(i => (
                <div key={i.id} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/20 transition-colors">
                  <ChannelBadge channel={i.channel} showLabel={false} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[12px] font-600 text-foreground">{i.citizen}</span>
                      <StatusBadge status={i.status} />
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate mb-1">{i.topic}</div>
                    <SentimentBadge sentiment={i.sentiment} />
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[11px] font-tabular text-muted-foreground">{i.time}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{i.agent}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Announcements */}
        <div className="col-span-12 xl:col-span-3">
          <div className="card-elevated overflow-hidden h-full">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
              <div className="p-1.5 rounded-lg bg-amber-50">
                <Icon name="MegaphoneIcon" size={13} className="text-amber-600" />
              </div>
              <h2 className="text-[13px] font-700">Announcements</h2>
            </div>
            <div className="divide-y divide-border">
              {announcements.map((a, i) => (
                <div key={i} className="px-4 py-3">
                  <div className="flex items-start gap-2 mb-1">
                    <Icon
                      name={a.type === 'warning' ? 'ExclamationTriangleIcon' : 'InformationCircleIcon'}
                      size={13}
                      className={`flex-shrink-0 mt-0.5 ${a.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`}
                    />
                    <div>
                      <div className="text-[12px] font-600 text-foreground mb-0.5">{a.title}</div>
                      <div className="text-[11px] text-muted-foreground leading-relaxed">{a.desc}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">{a.time}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sentiment Overview */}
        <div className="col-span-12">
          <div className="card-elevated p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1.5 rounded-lg bg-blue-50">
                <Icon name="FaceSmileIcon" size={13} className="text-blue-600" />
              </div>
              <h2 className="text-[13px] font-700">Citizen Sentiment Overview — Today</h2>
              <span className="ai-badge ml-1">AI</span>
              <span className="text-[11px] text-muted-foreground ml-auto">Based on 12,842 interactions</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              {[
                { sentiment: 'Positive' as SentimentType, count: 3842, pct: 30 },
                { sentiment: 'Satisfied' as SentimentType, count: 2568, pct: 20 },
                { sentiment: 'Neutral' as SentimentType, count: 3205, pct: 25 },
                { sentiment: 'Concerned' as SentimentType, count: 1541, pct: 12 },
                { sentiment: 'Frustrated' as SentimentType, count: 898, pct: 7 },
                { sentiment: 'Angry' as SentimentType, count: 513, pct: 4 },
                { sentiment: 'Disappointed' as SentimentType, count: 275, pct: 2 },
              ].map(s => (
                <div key={s.sentiment} className="card-elevated p-3 text-center">
                  <div className="text-2xl mb-1">{s.sentiment === 'Positive' ? '😊' : s.sentiment === 'Satisfied' ? '🙂' : s.sentiment === 'Neutral' ? '😐' : s.sentiment === 'Concerned' ? '😕' : s.sentiment === 'Frustrated' ? '😣' : s.sentiment === 'Angry' ? '😠' : '😞'}</div>
                  <div className="text-[16px] font-700 font-tabular text-foreground">{s.pct}%</div>
                  <div className="text-[10px] text-muted-foreground mb-1.5">{s.sentiment}</div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${s.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
